import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";

// Module-level guard: survives React 18 StrictMode double-mount AND component re-renders.
// Keyed by session_id so refreshing the page with a new session_id still works.
const consumed = new Set();

export default function AuthCallback() {
  const localGuard = useRef(false);
  const navigate = useNavigate();
  const { setUser, refresh } = useAuth();

  useEffect(() => {
    if (localGuard.current) return;
    localGuard.current = true;

    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const session_id = params.get("session_id");
    console.log("[auth] callback start, session_id present:", !!session_id);

    if (!session_id) {
      // No session_id — silently return to login (no error toast, this can happen on remount).
      navigate("/login", { replace: true });
      return;
    }

    // Clear the hash from the URL immediately so a re-mount doesn't re-trigger exchange.
    window.history.replaceState(null, "", "/");

    if (consumed.has(session_id)) {
      console.log("[auth] session_id already consumed in this tab — skipping");
      // The first invocation will handle navigation; we just no-op.
      return;
    }
    consumed.add(session_id);

    (async () => {
      try {
        // NOTE: withCredentials=false here because the K8s ingress overrides
        // Access-Control-Allow-Origin to '*', which browsers reject when credentials
        // are included. The auth/session response includes a session_token in the body
        // which we persist in localStorage and send as Bearer for subsequent calls.
        const r = await axios.post(
          `${API}/auth/session`,
          { session_id },
          { withCredentials: false, headers: { "Content-Type": "application/json" } }
        );
        console.log("[auth] session exchange OK:", r.data?.email, "role:", r.data?.role);

        // Persist session_token fallback so the axios interceptor sends it as Bearer.
        if (r.data?.session_token) {
          window.localStorage.setItem("garnacha_token", r.data.session_token);
        }

        // Set the user from the response (instant, no need to wait for /me).
        setUser(r.data);

        // Best-effort /me confirmation (background) — but do NOT overwrite user on failure.
        try {
          await refresh();
        } catch {
          /* ignore — we already have valid user from auth/session */
        }

        toast.success(`Welcome, ${r.data.name || r.data.email}`);
        navigate("/", { replace: true });
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail || `Login failed (status ${status || "network"})`;
        console.error("[auth] exchange failed:", status, detail);
        // 401 (Invalid OAuth session) and 400 (session_id required) typically mean
        // the session_id was already consumed by a prior invocation (e.g. StrictMode race
        // in stale cached bundles). Don't bounce the user; let the first call's success persist.
        const isRace = status === 401 || status === 400;
        if (!isRace) {
          consumed.delete(session_id);
          toast.error(detail);
          navigate("/login", { replace: true, state: { error: detail } });
        } else {
          console.warn("[auth] suppressing race-condition error; first call likely succeeded");
          // Best-effort: try to read existing session via /me before deciding
          try {
            const me = await refresh();
            if (me) {
              navigate("/", { replace: true });
              return;
            }
          } catch {/* ignore */}
          // No valid session — go back to login silently (no toast)
          navigate("/login", { replace: true });
        }
      }
    })();
  }, [navigate, setUser, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F7F1E8]" data-testid="auth-callback">
      <div className="text-sm text-[#6C757D]">Signing you in…</div>
    </div>
  );
}
